import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingCart, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useCompare } from '../context/CompareContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ProductCard = ({ product, onWishlistChange }) => {
  const { user, token } = useAuth();
  const { addToCart } = useCart();
  const { addToCompare, isInCompare, removeFromCompare } = useCompare();
  const navigate = useNavigate();
  const [isWishlisted, setIsWishlisted] = React.useState(false);
  const [addingToCart, setAddingToCart] = React.useState(false);

  const basePrice = product.variants?.[0]?.price || 0;
  // All variant sizes joined for display
  const allSizes = product.variants?.map(v => v.size).join(', ') || '';
  const inCompare = isInCompare(product.id);

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Navigate to product detail so the customer can choose size/quantity
    navigate(`/products/${product.id}`);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user || !token) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      if (isWishlisted) {
        await axios.delete(`${API_URL}/wishlist/${product.id}`);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API_URL}/wishlist/${product.id}`);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
      }
      onWishlistChange?.();
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (inCompare) {
      removeFromCompare(product.id);
      toast.success('Removed from compare');
    } else {
      const result = addToCompare(product);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    }
  };

  return (
    <Link to={`/products/${product.id}`} className="group block">
      <div className="product-card flex flex-row lg:flex-col bg-white border border-stone-100 rounded-2xl overflow-hidden hover:border-stone-200 hover:shadow-lg transition-all duration-300 h-full">
        {/* Image Container */}
        <div className="relative w-32 sm:w-40 lg:w-full aspect-square overflow-hidden bg-stone-50 flex-shrink-0">
          <img
            src={product.images?.[0] || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400'}
            alt={product.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          
          {/* Badges - Desktop Only (they look messy on tiny mobile images) */}
          <div className="absolute top-3 left-3 hidden lg:flex flex-col gap-2">
            {product.is_organic && <span className="badge-organic">Organic</span>}
            {product.is_featured && <span className="badge-featured">Featured</span>}
          </div>

          {/* Quick Actions - Desktop Only */}
          <div className="absolute top-3 right-3 hidden lg:flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
            <Button
              size="icon"
              variant="secondary"
              className="h-9 w-9 rounded-full bg-white/90 hover:bg-white shadow-md"
              onClick={handleWishlist}
            >
              <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-red-500 text-red-500' : ''}`} />
            </Button>
            <Button
              size="icon"
              variant="secondary"
              className={`h-9 w-9 rounded-full shadow-md ${inCompare ? 'bg-primary text-white hover:bg-primary/90' : 'bg-white/90 hover:bg-white'}`}
              onClick={handleCompare}
            >
              <Scale className="h-4 w-4" />
            </Button>
          </div>

          {/* Add to Cart Overlay - Desktop Only */}
          <div className="absolute inset-x-0 bottom-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300 hidden lg:block">
            <Button
              className="w-full bg-primary hover:bg-primary/90 text-white rounded-full"
              onClick={handleAddToCart}
              disabled={addingToCart}
            >
              <ShoppingCart className="h-4 w-4 mr-2" />
              {addingToCart ? 'Adding...' : 'Add to Cart'}
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-3 sm:p-4 flex flex-col justify-between min-w-0">
          <div>
            {/* Mobile Header: Name + Wishlist */}
            <div className="flex justify-between items-start gap-2">
              <h3 className="font-medium text-stone-900 line-clamp-2 text-sm sm:text-base lg:text-lg group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <button 
                onClick={handleWishlist}
                className="lg:hidden p-1.5 rounded-full hover:bg-stone-50 transition-colors flex-shrink-0"
              >
                <Heart className={`h-4 w-4 ${isWishlisted ? 'fill-red-500 text-red-500' : 'text-stone-400'}`} />
              </button>
            </div>

            {/* Sizes/Variants — show all available sizes */}
            {product.variants?.length > 0 && (
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                {product.variants.length === 1
                  ? product.variants[0].size
                  : allSizes}
              </p>
            )}

            {/* Desktop-only certifications */}
            {product.certifications?.length > 0 && (
              <div className="hidden lg:flex gap-1 mt-2">
                {product.certifications.slice(0, 2).map((cert, idx) => (
                  <span key={idx} className="text-xs text-muted-foreground bg-stone-100 px-2 py-0.5 rounded-full">
                    {cert}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Price & Mobile Add to Cart */}
          <div className="mt-2 flex items-center justify-between lg:block">
            <div className="flex items-baseline gap-1 lg:mt-3">
              <span className="text-base sm:text-lg lg:text-xl font-semibold text-primary">
                ₹{basePrice}
              </span>
              {product.variants?.[0]?.size && (
                <span className="hidden lg:inline text-sm text-muted-foreground">
                  / {product.variants[0].size}
                </span>
              )}
            </div>
            
            <Button
              size="sm"
              className="lg:hidden h-8 px-3 bg-primary hover:bg-primary/90 text-white rounded-lg text-xs"
              onClick={handleAddToCart}
              disabled={addingToCart}
            >
              <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
              Add
            </Button>
          </div>
        </div>
      </div>
    </Link>
  );
};

